type A11yKey = 'contrast' | 'text' | 'dyslexia';
type A11yState = Record<A11yKey, boolean>;

const A11Y_APPLIED_EVENT = 'white:a11y-applied';
const html = document.documentElement;

interface Option {
  key: A11yKey;
  attr: string;
  value: string;
  read: () => boolean;
  write: (on: boolean) => void;
}

const options: Option[] = [
  {
    key: 'contrast',
    attr: 'data-theme',
    value: 'contrast',
    read: () => localStorage.getItem('theme') === 'contrast',
    write: (on) => localStorage.setItem('theme', on ? 'contrast' : ''),
  },
  {
    key: 'text',
    attr: 'data-font',
    value: 'enlarged',
    read: () => localStorage.getItem('enlarged') === 'true',
    write: (on) => localStorage.setItem('enlarged', on ? 'true' : 'false'),
  },
  {
    key: 'dyslexia',
    attr: 'data-dyslexia',
    value: 'true',
    read: () => localStorage.getItem('dyslexia') === 'true',
    write: (on) => localStorage.setItem('dyslexia', on ? 'true' : 'false'),
  },
];

const applyTo = (target: Element, state: A11yState) => {
  options.forEach((opt) => {
    if (state[opt.key]) {
      target.setAttribute(opt.attr, opt.value);
    } else {
      target.removeAttribute(opt.attr);
    }
  });
};

const notifyA11yApplied = (state: A11yState) => {
  window.dispatchEvent(new CustomEvent(A11Y_APPLIED_EVENT, {
    detail: { ...state },
  }));
};

const readSavedState = (): A11yState => ({
  contrast: options.find((o) => o.key === 'contrast')?.read() === true,
  text: options.find((o) => o.key === 'text')?.read() === true,
  dyslexia: options.find((o) => o.key === 'dyslexia')?.read() === true,
});

const getPreviewAttributeValue = (key: A11yKey, isEnabled: boolean): string => {
  if (isEnabled) {
    return options.find((opt) => opt.key === key)?.value ?? '';
  }

  if (key === 'text' || key === 'contrast') {
    return 'default';
  }

  if (key === 'dyslexia') {
    return 'false';
  }

  return '';
};

const applyToPreview = (preview: Element | null, state: A11yState) => {
  if (!preview) {
    return;
  }

  options.forEach((opt) => {
    preview.setAttribute(opt.attr, getPreviewAttributeValue(opt.key, state[opt.key]));
  });
};

const getCheckbox = (key: A11yKey) => (
  document.querySelector<HTMLInputElement>(
    `[data-a11y-option="${key}"]`,
  )
);
// Apply saved state immediately so first paint uses the right attributes.
const modalState: A11yState = readSavedState();
applyTo(html, modalState);
notifyA11yApplied(modalState);

// Defer only DOM-dependent behavior.
export default function initA11yControls() {
  const preview = document.getElementById('a11y-preview');
  const saveBtn = document.getElementById('a11y-save');

  Alpine.data('a11y', () => ({
    open: false,
    dialog: null as unknown as HTMLDialogElement,
    init() {
      this.dialog = this.$refs.a11yDialog as HTMLDialogElement;
      this.open = this.dialog.open;
      this.dialog.addEventListener('toggle', () => {
        this.open = this.dialog.open;
      });
    },
  }));

  options.forEach((opt) => {
    const checkbox = getCheckbox(opt.key);
    if (!checkbox) {
      return;
    }

    checkbox.checked = modalState[opt.key];

    checkbox.addEventListener('change', () => {
      modalState[opt.key] = checkbox.checked;
      applyToPreview(preview, modalState);
    });
  });

  applyToPreview(preview, modalState);

  saveBtn?.addEventListener('click', () => {
    applyTo(html, modalState);
    notifyA11yApplied(modalState);

    options.forEach((opt) => {
      opt.write(modalState[opt.key]);
    });
  });
}
